/**
 * Admin page for escrow deployments: register a deployment, check it against the chain again,
 * and choose which one receives new campaigns (specification §5.7). Starting or stopping new
 * campaigns changes where brands deposit, so the page asks first.
 */

import { Boxes } from "lucide-react";
import { type FormEvent, useState } from "react";

import {
  type Deployment,
  useCheckDeployment,
  useDeployments,
  useRegisterDeployment,
  useSetAcceptsNewCampaigns,
} from "../../admin/deployments.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { Checkbox } from "../../components/forms.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import {
  Badge,
  EmptyState,
  ErrorMessage,
  Fact,
  Loading,
  PageHeader,
  styles,
} from "../../components/ui.js";
import { useAppConfig } from "../../config.js";
import { formatUtc } from "../../format.js";
import { chainName, explorerAddressUrl } from "../../wallet/wagmi.js";

export function AdminDeployments() {
  return (
    <RequireAdmin>
      <DeploymentsPage />
    </RequireAdmin>
  );
}

function DeploymentsPage() {
  const { chainId } = useAppConfig();
  const deployments = useDeployments();

  return (
    <section>
      <PageHeader
        eyebrow="Admin"
        title="Escrow deployments"
        documentTitle="Escrow deployments · Admin"
      >
        Brands deposit into the deployment that receives new campaigns. A deployment is registered
        only after it passes the on-chain checks. They cover the released escrow code, Morcast's
        owner, settler and treasury, its creation block, and the accepted campaign tokens.
      </PageHeader>

      <RegisterForm />

      <h2 className="mt-12 text-xl font-semibold tracking-tight">
        Registered on {chainName(chainId)}
      </h2>
      <div className="mt-4">
        {deployments.isPending && <Loading />}
        {deployments.isError && <ErrorMessage error={deployments.error} />}
        {deployments.data?.length === 0 && (
          <EmptyState icon={Boxes} title="No deployment is registered yet." />
        )}
        {deployments.data && deployments.data.length > 0 && (
          <ul className="space-y-4">
            {deployments.data.map((deployment) => (
              <DeploymentCard key={deployment.address} deployment={deployment} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Form to register a deployment. */
function RegisterForm() {
  const register = useRegisterDeployment();
  const [address, setAddress] = useState("");
  const [block, setBlock] = useState("");
  const [acceptsNewCampaigns, setAcceptsNewCampaigns] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    register.mutate(
      { address: address.trim(), deploymentBlock: Number(block), acceptsNewCampaigns },
      {
        onSuccess: () => {
          setAddress("");
          setBlock("");
          setAcceptsNewCampaigns(false);
        },
      },
    );
  }

  return (
    <form onSubmit={submit} aria-labelledby="register-title" className={`mt-8 ${styles.card}`}>
      <h2 id="register-title" className="font-semibold">
        Register a deployment
      </h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_12rem]">
        <label className="text-sm font-medium">
          Escrow address
          <input
            required
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            pattern="0x[0-9a-fA-F]{40}"
            placeholder="0x…"
            className={`${styles.input} font-mono`}
          />
        </label>
        <label className="text-sm font-medium">
          Creation block
          <input
            required
            value={block}
            onChange={(event) => setBlock(event.target.value)}
            inputMode="numeric"
            pattern="[0-9]+"
            className={styles.input}
          />
        </label>
      </div>
      <label className="mt-4 flex w-fit cursor-pointer items-center gap-2 text-sm">
        <Checkbox
          checked={acceptsNewCampaigns}
          onChange={(event) => setAcceptsNewCampaigns(event.target.checked)}
        />
        Receive new campaigns
      </label>
      <div className="mt-5 flex items-center gap-4">
        <button type="submit" disabled={register.isPending} className={styles.primaryButton}>
          {register.isPending ? "Checking on-chain…" : "Register"}
        </button>
        {register.isSuccess && (
          <p role="status" className="text-sm text-emerald-700 dark:text-emerald-400">
            Registered.
          </p>
        )}
      </div>
      {register.isError && <ErrorMessage error={register.error} />}
    </form>
  );
}

/** One registered deployment with its actions. */
function DeploymentCard({ deployment }: { deployment: Deployment }) {
  const check = useCheckDeployment();
  const setAccepting = useSetAcceptsNewCampaigns();
  const [confirming, setConfirming] = useState(false);
  const stopping = deployment.acceptsNewCampaigns;
  const explorer = explorerAddressUrl(deployment.chainId, deployment.address);
  const busy = check.isPending || setAccepting.isPending;
  // The last action's error only: a later action clears the earlier one's.
  const error = check.error ?? setAccepting.error;

  return (
    <li className={styles.card}>
      <div className="flex flex-wrap items-center gap-2">
        {explorer ? (
          <a href={explorer} className="font-mono text-sm break-all hover:underline">
            {deployment.address}
          </a>
        ) : (
          <span className="font-mono text-sm break-all">{deployment.address}</span>
        )}
        {deployment.acceptsNewCampaigns && <Badge tone="green">Receives new campaigns</Badge>}
        {deployment.creationPaused && <Badge tone="amber">Creation paused on-chain</Badge>}
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
        <Fact label="Version">{deployment.version}</Fact>
        <Fact label="Creation block">{deployment.deploymentBlock.toLocaleString("en-US")}</Fact>
        <Fact label="Accepted tokens">
          {deployment.tokens.map((token) => token.symbol).join(", ") || "None"}
        </Fact>
        <Fact label="Last checked">{formatUtc(deployment.checkedAt)}</Fact>
      </dl>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setAccepting.reset();
            check.mutate(deployment.address);
          }}
          className={styles.secondaryButton}
        >
          {check.isPending ? "Checking on-chain…" : "Check again"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setConfirming(true)}
          className={styles.secondaryButton}
        >
          {stopping ? "Stop new campaigns" : "Start new campaigns"}
        </button>
      </div>
      {error && <ErrorMessage error={error} />}
      <ConfirmDialog
        open={confirming}
        title={stopping ? "Stop new campaigns here?" : "Receive new campaigns here?"}
        confirmLabel={stopping ? "Stop new campaigns" : "Start new campaigns"}
        cancelLabel={stopping ? "Keep receiving them" : "Not now"}
        tone={stopping ? "danger" : "primary"}
        onConfirm={() => {
          check.reset();
          setAccepting.mutate({
            address: deployment.address,
            acceptsNewCampaigns: !stopping,
          });
        }}
        onClose={() => setConfirming(false)}
      >
        {stopping ? (
          <p>
            Morcast stops accepting requests for this escrow. Campaigns already funded on it go on
            as before. Until another deployment starts receiving new campaigns, brands cannot send
            requests.
          </p>
        ) : (
          <p>
            Morcast checks this escrow on-chain again first. Requests accepted from then on name it,
            and brands deposit their budgets into it. Only one deployment receives new campaigns at
            a time, so stop the current one first.
          </p>
        )}
      </ConfirmDialog>
    </li>
  );
}
